import { Alert, Box, Button, Card, Code, Group, Modal, Select, Stack, Switch, TextInput } from "@mantine/core";
import { IValueLabel } from "@mctypes/global.types";
import { IProjectMemberApiItem } from "@schemas/projectSchema";
import { IconRefresh } from "@tabler/icons-react";
import * as validateEmail from "email-validator";
import { useState } from "react";
import { useSelector } from "react-redux";
import {
  GetProjectMembersApiResponse,
  PostProjectMaconomyMembersSyncApiResponse,
  useGetProjectMembersQuery,
  usePostProjectMaconomyMembersSyncMutation,
} from "../../../../../openapi";
import { CreateNewButton } from "../../../../components";
import { InfoCircle, MCLoader } from "../../../../components/";
import {
  selectUserMember,
  setShowFailedMessageThunk,
  setShowSuccessMessageThunk,
} from "../../../../global/globalSlice";
import { selectCurrentProjectId } from "../../../../global/globalSliceCommon";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { EMAIL_REGEX } from "../../../../mc-shared/sharedConstants";
import { trpcSlice } from "../../../../trpcSlice";
import { loadProject } from "../../../actions";
import { MEMBER_GROUPS } from "../../../globalConstants";
import { useGetProjectQuery } from "../../../projectRTK";
import { isDeveloper, isEmpty } from "../../../utils";
import { formatDateYmdDotsWithTime } from "../selectors";
import { doAddExternalMemberToProject } from "../restQueries";

export enum DuplicateWarningEnum {
  TRUE = "TRUE",
  FALSE = "FALSE",
  ISPASSIVE = "ISPASSIVE",
}
const AddNewMember = () => {
  const dispatch = useAppDispatch();

  const [group, setGroup] = useState(null);

  const projectId = useAppSelector(selectCurrentProjectId);
  const { data: project } = useGetProjectQuery(projectId);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [showAddMemberForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState<DuplicateWarningEnum>(DuplicateWarningEnum.FALSE);
  const [emailIsMulticonsult, setEmailIsMulticonsult] = useState(false);
  const [showMaconomyAccessSyncModal, setShowMaconomyAccessSyncModal] = useState(false);
  const [maconomyAccessSyncDryRun, setMaconomyAccessSyncDryRun] = useState(true);
  const [maconomyAccessSyncResult, setMaconomyAccessSyncResult] =
    useState<PostProjectMaconomyMembersSyncApiResponse | null>(null);
  const [syncProjectMembersFromMaconomyAccess, { isLoading: maconomyAccessSyncLoading }] =
    usePostProjectMaconomyMembersSyncMutation();

  const isTemplateProject = project?.maconomy?.projectNo === 100;

  const emailIsLongEmailMulticonsult = getFirstPartOfEmail(email)?.split(".").length > 1;

  const {
    data: members = [],
    isLoading: membersLoading,
    isError: errorMembers,
  } = useGetProjectMembersQuery({ projectId }, { skip: project == null });

  const sendInvitationToExternalUser = async () => {
    setLoading(true);
    try {
      await doAddExternalMemberToProject(email, group?.value, project._id, name);
      setEmail("");
      setName("");
      setGroup(null);
      setEmailIsMulticonsult(false);
      setShowAddForm(false);

      dispatch(setShowSuccessMessageThunk("Invitasjon sendt"));
      dispatch(loadProject(project._id));
      dispatch(trpcSlice.util.invalidateTags(["TrpcMembers"]));
    } catch (error) {
      dispatch(setShowFailedMessageThunk("Kunne ikke sende invitasjon"));
    } finally {
      setLoading(false);
    }
  };

  const checkMcEmail = (email: string): boolean => {
    return emailIsMulticonsult === true && isTemplateProject && emailIsLongEmailMulticonsult === false;
  };

  const emailIsValid = validateEmail.validate(email) === true;

  const sendIsEnabled =
    (duplicateWarning === DuplicateWarningEnum.FALSE &&
      validateEmail.validate(email) === true &&
      checkMcEmail(email) === true) ||
    (isEmpty(name) === false && isEmpty(group) === false);

  const toggleForm = () => {
    setShowAddForm(!showAddMemberForm);
  };

  const syncMembersFromMaconomy = async () => {
    await syncMembersFromMaconomyAccess(false);
  };

  const userMember = useAppSelector(selectUserMember);
  const user = useAppSelector((state) => state.globalReducer.user);
  const showDeveloperSync = user?._id != null && isDeveloper(user._id) && isTemplateProject === false;

  const syncMembersFromMaconomyAccess = async (dryRun = maconomyAccessSyncDryRun) => {
    const result = await syncProjectMembersFromMaconomyAccess({
      body: {
        projectId: project._id,
        dryRun,
      },
    }).unwrap();

    setMaconomyAccessSyncResult(result);

    if (!dryRun) {
      dispatch(loadProject(project._id));
      dispatch(setShowSuccessMessageThunk("Synkronisering vellykket"));
      dispatch(trpcSlice.util.invalidateTags(["TrpcMembers"]));
    }
  };

  return (
    <>
      {(loading || maconomyAccessSyncLoading) && <MCLoader loading />}
      {loading === false && maconomyAccessSyncLoading === false && (
        <Group gap="xs" wrap="nowrap">
          {userMember?.group !== "CLIENT" && (
            <CreateNewButton size="sm" onClick={() => setShowAddForm(!showAddMemberForm)} tooltip="Legg til bruker" />
          )}
          {isTemplateProject === false && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => syncMembersFromMaconomy()}
                leftSection={<IconRefresh size={16} />}
              >
                Synkroniser prosjektmedlemmer fra Maconomy
              </Button>
              <InfoCircle
                text={`Siste synkronisering: ${formatDateYmdDotsWithTime(project?.lastMaconomySync)}`}
              />
            </>
          )}
          {showDeveloperSync && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setMaconomyAccessSyncResult(null);
                setMaconomyAccessSyncDryRun(true);
                setShowMaconomyAccessSyncModal(true);
              }}
              leftSection={<IconRefresh size={16} />}
            >
              Dry-run Maconomy-synk
            </Button>
          )}
        </Group>
      )}
      <Modal
        opened={showAddMemberForm && loading === false}
        onClose={() => {
          setShowAddForm(false);
          setEmailIsMulticonsult(false);
        }}
        withCloseButton
        yOffset={140}
        size="sm"
        title="Legg til medlem"
      >
        <AddForm
          projectNo={project?.maconomy?.projectNo}
          group={group}
          emailIsMulticonsult={emailIsMulticonsult}
          sendInvitation={sendInvitationToExternalUser}
          email={email}
          isDuplicate={duplicateWarning}
          sendIsEnabled={sendIsEnabled}
          setEmail={(value) => {
            setEmail(value);
            setEmailIsMulticonsult(value.endsWith("@multiconsult.no"));
          }}
          name={name}
          setName={setName}
          setGroup={setGroup}
          members={members}
        />
      </Modal>
      <Modal
        opened={showMaconomyAccessSyncModal}
        onClose={() => setShowMaconomyAccessSyncModal(false)}
        withCloseButton
        yOffset={100}
        size="xl"
        title="Synkroniser prosjektmedlemmer fra Maconomy"
      >
        <Stack gap="sm">
          <Switch
            checked={maconomyAccessSyncDryRun}
            onChange={(event) => setMaconomyAccessSyncDryRun(event.currentTarget.checked)}
            label="Dry-run"
          />
          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={() => setShowMaconomyAccessSyncModal(false)}
              disabled={maconomyAccessSyncLoading}
            >
              Lukk
            </Button>
            <Button onClick={() => syncMembersFromMaconomyAccess()} loading={maconomyAccessSyncLoading}>
              Kjør synk
            </Button>
          </Group>
          {maconomyAccessSyncResult != null && (
            <Code block style={{ maxHeight: "50vh", overflow: "auto", whiteSpace: "pre-wrap" }}>
              {JSON.stringify(maconomyAccessSyncResult, null, 2)}
            </Code>
          )}
        </Stack>
      </Modal>
      {emailIsLongEmailMulticonsult && isTemplateProject && emailIsValid && (
        <Alert color="yellow" variant="light">
          Angi kort e-post
        </Alert>
      )}
    </>
  );
};

interface AddFormProps {
  emailIsMulticonsult: boolean;
  sendIsEnabled: boolean; // legacy parent prop
  setEmail: (val: string) => void;
  email: string;
  isDuplicate: DuplicateWarningEnum;
  sendInvitation: () => Promise<void>;
  name: string;
  setName: (val: string) => void;
  setGroup: (group: IValueLabel) => void;
  group: IValueLabel | null;
  projectNo: number;
  members: GetProjectMembersApiResponse;
}

const AddForm: React.FC<AddFormProps> = ({
  emailIsMulticonsult,
  setEmail,
  email,
  sendInvitation,
  name,
  setName,
  setGroup,
  group,
  projectNo,
  members,
}) => {
  const member = useSelector(selectUserMember);
  const [emailValidation, setEmailValidation] = useState<boolean | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState<DuplicateWarningEnum>(DuplicateWarningEnum.FALSE);
  const [emailTouched, setEmailTouched] = useState(false);

  const validateEmailOnBlur = (val: string) => {
    setEmailTouched(true);
    if (val?.trim().length === 0) {
      setEmailValidation(null);
      setDuplicateWarning(DuplicateWarningEnum.FALSE);
      return;
    }
    const isValid = EMAIL_REGEX.test(val);
    setEmailValidation(isValid);
    if (isValid) {
      const existing = members.find(
        (m) =>
          m.user?.email?.toLowerCase() === val.toLowerCase() || m.user?.fullEmail?.toLowerCase() === val.toLowerCase(),
      );
      if (existing) {
        setDuplicateWarning(existing.isPassive ? DuplicateWarningEnum.ISPASSIVE : DuplicateWarningEnum.TRUE);
      } else {
        setDuplicateWarning(DuplicateWarningEnum.FALSE);
      }
    } else {
      setDuplicateWarning(DuplicateWarningEnum.FALSE);
    }
  };

  const emailIsValid = emailValidation === true;
  const noDuplicate = duplicateWarning === DuplicateWarningEnum.FALSE;
  const isTemplateProject = projectNo === 100;
  const nameValid = name?.trim().length > 0;
  const groupValid = group != null;
  const canSend = emailIsValid && noDuplicate && (emailIsMulticonsult ? isTemplateProject : nameValid && groupValid);
  const groupOptions = getEnabledMemberGroups(MEMBER_GROUPS, member);

  return (
    <Box>
      <Card p="sm" bg={"#fafafb"} bd={"1px solid var(--mantine-color-warmgray-2)"}>
        <Stack gap="xs">
          <TextInput
            label="Email"
            type="email"
            placeholder="Skriv inn epost"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value?.toLowerCase())}
            onBlur={(e) => validateEmailOnBlur(e.currentTarget.value?.toLowerCase())}
          />
          {emailTouched && emailValidation === false && (
            <Alert color="red" py={"xs"}>
              {email.includes("@")
                ? "E-post må ha et domenenavn etter @ (f.eks. navn@firma.no)"
                : "E-post må inneholde @"}
            </Alert>
          )}
          {!emailIsMulticonsult && (
            <>
              <TextInput
                label="Navn"
                placeholder="Skriv inn navn"
                value={name || ""}
                onChange={(e) => setName(e.currentTarget.value)}
              />
              <Select
                label="Gruppe"
                placeholder="Velg gruppe"
                data={groupOptions.map((g) => ({ value: g.value, label: g.label }))}
                value={group?.value || null}
                onChange={(val) => {
                  const selected = groupOptions.find((g) => g.value === val);
                  if (selected) setGroup(selected as IValueLabel);
                }}
              />
            </>
          )}
          <Button color="mc-primary" mt={"md"} variant="filled" onClick={() => sendInvitation()} disabled={!canSend}>
            Sende
          </Button>
          {emailIsMulticonsult && projectNo !== 100 && (
            <Alert py={"xs"}>
              Multiconsult-ansatte får tilgang via Maconomy. Hvis personen har tilgang i Maconomy, men ikke er oppført
              her, kan du kontakte mcapps@multiconsult.no
            </Alert>
          )}
          {emailTouched && duplicateWarning !== DuplicateWarningEnum.FALSE && (
            <Alert variant="light" py={"xs"}>
              {`E-post er allerede invitert${
                duplicateWarning === DuplicateWarningEnum.ISPASSIVE ? ", men er satt til 'passiv'" : ""
              }.`}
            </Alert>
          )}
        </Stack>
      </Card>
    </Box>
  );
};

export default AddNewMember;

const getEnabledMemberGroups = (memberGroups: IValueLabel[], member: IProjectMemberApiItem): IValueLabel[] => {
  if (member.group === "ADMIN") {
    return memberGroups.filter((group) => group.value !== "INTERNAL");
  }
  return memberGroups.filter((group) => group.value !== "INTERNAL" && group.value !== "ADMIN");
};

const getFirstPartOfEmail = (email: string): string => {
  return email.split("@")[0];
};
